/**
 * Liga a web app ao shell nativo. Ver docs/10-desktop.md.
 *
 * Tudo aqui é opcional: num browser `native` é `null` e nada disto corre. É
 * o que permite uma só build servir os dois alvos.
 */
import { useStore } from '@tasca/client-sdk';
import type { VoiceClient } from '@tasca/client-sdk';
import { codeToAccelerator, native } from './desktop.js';

/**
 * Registra o push-to-talk no SO e mantém o registo alinhado com as
 * preferências.
 *
 * Devolve a função de cancelamento. Reage a mudanças de tecla e de modo
 * porque o utilizador pode mudar as duas nas definições de voz, e um atalho
 * global que ficou a apontar para a tecla antiga é pior que nenhum.
 */
function wirePushToTalk(voice: VoiceClient): () => void {
  if (!native) return () => {};

  let bound: string | null = null;
  let disposed = false;
  const offChange = native.pushToTalk.onChange((down) => voice.setPushToTalkActive(down));

  async function sync(mode: string, key: string): Promise<void> {
    if (disposed) return;
    const wanted = mode === 'ptt' ? codeToAccelerator(key) : null;
    if (wanted === bound) return;

    if (bound !== null) {
      await native!.pushToTalk.unbind().catch(() => {});
      bound = null;
      voice.setGlobalPushToTalk(false);
    }
    if (wanted === null) {
      // Modo por voz, ou uma tecla que não sabemos traduzir. No segundo caso
      // o fallback do browser ainda serve com a janela em foco.
      return;
    }

    const okBound = await native!.pushToTalk.bind(wanted).catch(() => false);
    if (disposed) return;
    bound = okBound ? wanted : null;
    voice.setGlobalPushToTalk(okBound);
  }

  const settings = useStore.getState().voiceSettings;
  void sync(settings.mode, settings.pttKey);

  const unsubscribe = useStore.subscribe((state, previous) => {
    if (
      state.voiceSettings.mode === previous.voiceSettings.mode &&
      state.voiceSettings.pttKey === previous.voiceSettings.pttKey
    ) {
      return;
    }
    void sync(state.voiceSettings.mode, state.voiceSettings.pttKey);
  });

  return () => {
    disposed = true;
    unsubscribe();
    offChange();
    if (bound !== null) void native!.pushToTalk.unbind().catch(() => {});
  };
}

/**
 * Badge do tray e notificações nativas.
 *
 * O badge vem de `totalUnread()` e só se escreve quando o número MUDA:
 * pintar o ícone do tray a cada mensagem é trabalho do compositor do SO por
 * nada.
 *
 * As notificações só saem com a janela escondida. Uma notificação do SO por
 * cima da app que está à frente é ruído, e é a primeira coisa que as pessoas
 * desligam.
 */
function wireNotifications(): () => void {
  if (!native) return () => {};

  let lastCount = -1;
  let lastNotified: string | null = null;

  return useStore.subscribe((state, previous) => {
    const count = useStore.getState().totalUnread();
    if (count !== lastCount) {
      lastCount = count;
      void native!.setUnread(count).catch(() => {});
    }

    if (document.visibilityState === 'visible') return;
    if (state.messages === previous.messages) return;

    // A mensagem mais recente que acabou de chegar, de qualquer canal.
    let newest: { id: string; author: string; content: string; channelId: string } | null = null;
    for (const [channelId, list] of state.messages) {
      const last = list[list.length - 1];
      if (!last || last.pending) continue;
      if (last.author?.id === state.me?.id) continue;
      if (newest && last.id.length < newest.id.length) continue;
      if (newest && last.id.length === newest.id.length && last.id <= newest.id) continue;
      newest = {
        id: last.id,
        author: last.author?.displayName ?? 'Alguém',
        content: last.content,
        channelId,
      };
    }
    if (!newest || newest.id === lastNotified) return;
    lastNotified = newest.id;

    const channel = [...state.channels.values()]
      .flat()
      .find((c) => c.id === newest!.channelId);

    void native!
      .notify({
        title: channel?.name ? `${newest.author} em #${channel.name}` : newest.author,
        // Uma notificação não é a mensagem: cortar é melhor que encher o
        // ecrã com um parágrafo.
        body: newest.content.slice(0, 180),
      })
      .catch(() => {});
  });
}

/**
 * Arranca a integração nativa. Devolve a função de cancelamento.
 *
 * Idempotente por construção — é chamada uma vez pelo App e os dois
 * subscritores são independentes.
 */
export function startDesktopIntegration(voice: VoiceClient): () => void {
  if (!native) return () => {};
  const stopPtt = wirePushToTalk(voice);
  const stopNotifications = wireNotifications();
  return () => {
    stopPtt();
    stopNotifications();
  };
}
